import { Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { Client } from 'src/app/entities/client/client';
import { catchError, map } from 'rxjs';
import { ClientRepository } from 'src/app/services/remote-data-service/repositories/person-repository/client.repository';
import { StoreService } from 'src/app/services/store-service/store.service';
import { Uuid } from 'src/app/entities/value-objects/uuid/uuid';
import { OperationType } from 'src/app/models/operation-types';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';

@Component({
  selector: 'app-clients-mutations',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent
  ],
  templateUrl: './clients-mutations.component.html',
  styleUrl: './clients-mutations.component.scss'
})
/**
 * Component that handles the creation and update of clients through a dialog.
 * It provides a form interface for both operations, reusing the same component
 * but adapting its behavior based on the operation type.
 */
export class ClientsMutationsComponent implements OnInit {
  // Dialog injection and configuration
  readonly dialogRef = inject(MatDialogRef<ClientsMutationsComponent>);
  data = inject(MAT_DIALOG_DATA).operation as OperationType;
  clientId = inject(MAT_DIALOG_DATA).id as string;

  // Component state
  clients: Client[] = [];
  title?: string;
  submitButton?: string;

  // Form definition with validation
  protected form = new FormGroup({
    name: new FormControl<string>('', Validators.required),
  });

  constructor(
    protected clientRep: ClientRepository,
    protected store: StoreService,
    private snackbarService: SnackbarService,
  ) {
    this.store.clients.subscribe((clients) => {
      this.clients = clients;
    });
  }

  /**
   * Initializes the component based on the operation type.
   * Sets up the form with existing data if updating a client.
   */
  ngOnInit(): void {
    switch (this.data) {
      case OperationType.CREATE:
        this.title = 'Create Client';
        this.submitButton = 'Create';
        break;
      case OperationType.UPDATE: {
        this.title = 'Update Client';
        this.submitButton = 'Update';
        const currentClient = this.clients.find(c => c.id === this.clientId);

        if (currentClient) {
          this.form.patchValue({
            name: currentClient.name
          });
        }
        break;
      }
    }
  }

  /**
   * Handles form submission for both create and update operations
   * Creates or updates a client based on the operation type
   */
  clientSubmit(): void {
    const form = this.form.value;

    const newClient = new Client(
      Uuid.generateId(),
      form.name || ''
    );

    const updatedClient = new Client(
      this.clientId,
      form.name || ''
    );

    switch (this.data) {
      case OperationType.CREATE:
        this.addClient(newClient);
        break;
      case OperationType.UPDATE: {
        this.updateClient(updatedClient);
        break;
      }
    }
  }

  /**
   * Creates a new client in the backend and updates the local store
   * Adds the new client to the beginning of the clients list
   */
  addClient(client: Client): void {
    this.clientRep.addNewClient(client).pipe(
      catchError(() => {
        return []
      }),
      map(() => {
        this.store.addEntity(client);
        this.snackbarService.showSuccess('Client created');
      })
    ).subscribe()
  }

  /**
   * Updates an existing client in the backend and refreshes the local store
   * Maintains the current clients list order
   */
  updateClient(client: Client): void {
    this.clientRep.updateClient(client).pipe(
      catchError(() => {
        return []
      }),
      map(() => {
        this.store.updateEntity(client);
        this.snackbarService.showSuccess('Client updated');
      })
    ).subscribe()
  }
}
